import { Form, FormikProvider, useFormik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorBoundary, useUploadAsset } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Modal } from '@treams/treams-ui';

import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';

type AnnouncementFragment = graphql.AnnouncementFragmentFragment;

type Props = {
	announcement?: AnnouncementFragment;
	onDismiss: () => void;
};

export default function AnnouncementModal({ announcement, onDismiss }: Props) {
	const { t } = useTranslation();
	const [error, attempt] = useErrorBoundary();

	const isEditing = !!announcement;

	// Fetch workspaces for filter selection
	const workspacesQuery = graphql.useWorkspacesBasicQuery({
		variables: {
			filters: { isArchived: false },
			first: 1000,
		},
	});

	const workspaceOptions = useMemo(() => {
		if (!workspacesQuery.data) return [];
		return workspacesQuery.data.workspacesV2.edges
			.filter((e) => e.node.id !== 'treams')
			.map(({ node }) => ({
				label: node.id,
				value: node.id,
			}));
	}, [workspacesQuery.data]);

	// Mutations
	const [createAnnouncement, { loading: creating }] = graphql.useCreateAnnouncementMutation();
	const [updateAnnouncement, { loading: updating }] = graphql.useUpdateAnnouncementMutation();

	// Asset upload
	const [uploadAsset, { loading: uploading }] = useUploadAsset();

	const handleImageUpload = async (file: File): Promise<{ id: string; url: string }> => {
		const asset = await uploadAsset(file);
		return { id: asset.id, url: asset.source.url };
	};

	const formik = useFormik({
		initialValues: {
			title: announcement?.title ?? [],
			enabled: announcement?.enabled ?? true,
			content: announcement?.content ?? [],
			displayType: announcement?.displayType ?? graphql.AnnouncementDisplayType.Modal,
			workspaceIDs: announcement?.filters.workspaces.map((w) => w.id) ?? [],
			excludedWorkspaceIDs: announcement?.filters.excludedWorkspaces.map((w) => w.id) ?? [],
			superUserOnly: announcement?.filters.superUserOnly ?? false,
			headerImage: announcement?.headerImage
				? { id: announcement.headerImage.id, url: announcement.headerImage.source.url }
				: undefined,
		},
		onSubmit: attempt(async (values) => {
			// Banners never store rich content or a header image
			const isBannerSubmit = values.displayType === graphql.AnnouncementDisplayType.Banner;

			if (isEditing) {
				await updateAnnouncement({
					variables: {
						input: {
							announcementID: announcement.id,
							title: values.title,
							enabled: values.enabled,
							content: isBannerSubmit ? [] : values.content,
							headerImageAssetID: isBannerSubmit ? null : values.headerImage?.id,
							displayType: values.displayType,
							filters: {
								workspaceIDs: values.workspaceIDs,
								excludedWorkspaceIDs: values.excludedWorkspaceIDs,
								superUserOnly: values.superUserOnly,
							},
						},
					},
				});
			} else {
				await createAnnouncement({
					variables: {
						input: {
							title: values.title,
							enabled: values.enabled,
							content: isBannerSubmit ? [] : values.content,
							headerImageAssetID: isBannerSubmit ? undefined : values.headerImage?.id,
							displayType: values.displayType,
							filters: {
								workspaceIDs: values.workspaceIDs,
								excludedWorkspaceIDs: values.excludedWorkspaceIDs,
								superUserOnly: values.superUserOnly,
							},
						},
					},
				});
			}
			onDismiss();
		}),
	});

	const isBanner = formik.values.displayType === graphql.AnnouncementDisplayType.Banner;

	const isLoading = creating || updating;
	const isContentEmpty =
		!isBanner && (!formik.values.content || formik.values.content.length === 0);

	return (
		<Modal width={700}>
			<Modal.Header
				title={isEditing ? t('announcements.edit.title') : t('announcements.create.title')}
				onClose={onDismiss}
			/>
			<Modal.Contents>
				<GraphQLErrorMessage error={error} />

				<FormikProvider value={formik}>
					<Form>
						<DetailsTableForm
							formValues={[
								{
									label: t('announcements.form.title'),
									name: 'title',
									type: DetailsTableFormValueTypes.Multilingual,
								},
								{
									label: t('announcements.form.enabled'),
									name: 'enabled',
									type: DetailsTableFormValueTypes.Switch,
								},
								{
									label: t('announcements.form.displayType'),
									name: 'displayType',
									type: DetailsTableFormValueTypes.Select,
									options: [
										{
											label: t('announcements.form.displayType.modal'),
											value: graphql.AnnouncementDisplayType.Modal,
										},
										{
											label: t('announcements.form.displayType.banner'),
											value: graphql.AnnouncementDisplayType.Banner,
										},
									],
								},
								...(isBanner
									? []
									: [
											{
												label: t('announcements.form.headerImage'),
												name: 'headerImage',
												type: DetailsTableFormValueTypes.ImageUpload,
												accept: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
												onUpload: handleImageUpload,
												uploading,
											},
											{
												label: t('announcements.form.content'),
												name: 'content',
												type: DetailsTableFormValueTypes.MultilingualQuill,
												hasMediaUpload: true,
											},
										]),
								{
									label: t('announcements.form.workspaces'),
									name: 'workspaceIDs',
									type: DetailsTableFormValueTypes.MultiSelect,
									options: workspaceOptions,
									loading: workspacesQuery.loading,
								},
								{
									label: t('announcements.form.excludedWorkspaces'),
									name: 'excludedWorkspaceIDs',
									type: DetailsTableFormValueTypes.MultiSelect,
									options: workspaceOptions,
									loading: workspacesQuery.loading,
								},
								{
									label: t('announcements.form.superUserOnly'),
									name: 'superUserOnly',
									type: DetailsTableFormValueTypes.Switch,
								},
							]}
						/>
					</Form>
				</FormikProvider>
			</Modal.Contents>
			<Modal.FormFooter
				small
				onCancel={onDismiss}
				onSave={formik.submitForm}
				saveTitle={isEditing ? t('common.save') : t('common.create')}
				disabled={isContentEmpty || isLoading}
			/>
		</Modal>
	);
}
